/**
 * Genera PDF de historia clínica completa.
 * Usa jsPDF para crear un documento formal con secciones bien organizadas.
 */
import jsPDF from 'jspdf';

export async function exportHistoriaClinicaPDF({
  paciente,
  expediente,
  consultas,
  recetas,
  estudios,
}) {
  const doc = new jsPDF({ unit: 'mm', format: 'letter' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margen = 15;
  let y = margen;

  // --- Encabezado ---
  doc.setFillColor(30, 58, 95);
  doc.rect(0, 0, pageWidth, 28, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Historia Clínica', margen, 14);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`NARA Care · Expediente ${expediente?.numero_expediente || '—'}`, margen, 21);
  doc.text(`Generado: ${new Date().toLocaleString('es')}`, pageWidth - margen, 21, { align: 'right' });

  y = 38;
  doc.setTextColor(30, 41, 59);

  // --- Datos del paciente ---
  y = seccion(doc, y, margen, pageWidth, '1. Datos del Paciente');
  y = linea(doc, y, margen, 'Nombre', paciente.nombre_completo);
  y = linea(doc, y, margen, 'Edad / Sexo', `${calcEdad(paciente.fecha_nacimiento)} años · ${paciente.sexo || '—'}`);
  y = linea(doc, y, margen, 'Fecha de nacimiento', fmt(paciente.fecha_nacimiento));
  y = linea(doc, y, margen, 'Tipo de sangre', paciente.tipo_sangre || '—');
  y = linea(doc, y, margen, 'Nacionalidad', paciente.nacionalidad || '—');
  y = linea(doc, y, margen, 'Lugar de origen', paciente.lugar_nacimiento || '—');
  y = linea(doc, y, margen, 'Lugar de residencia', paciente.lugar_residencia || '—');
  y = linea(doc, y, margen, 'Estado civil', paciente.estado_civil || '—');
  y = linea(doc, y, margen, 'Ocupación', paciente.ocupacion || '—');
  y = linea(doc, y, margen, 'Escolaridad', paciente.escolaridad || '—');
  y = linea(doc, y, margen, 'Religión', paciente.religion || '—');
  y = linea(doc, y, margen, 'Teléfono', paciente.telefono || '—');
  y = linea(doc, y, margen, 'Correo', paciente.correo_electronico || '—');
  y += 4;

  // --- Antecedentes ---
  if (expediente) {
    y = checkPage(doc, y, margen);
    y = seccion(doc, y, margen, pageWidth, '2. Antecedentes');

    y = subseccion(doc, y, margen, 'Heredo-familiares');
    const hf = expediente.antecedentes_heredo_familiares || {};
    const filas = formatearHeredoFamiliares(hf);
    for (const f of filas) {
      y = checkPage(doc, y, margen);
      y = texto(doc, y, margen, `• ${f}`);
    }
    if (filas.length === 0) y = texto(doc, y, margen, 'Sin datos registrados');
    y += 2;

    y = subseccion(doc, y, margen, 'Personales patológicos');
    const pp = expediente.antecedentes_personales_patologicos || {};
    y = linea(doc, y, margen, 'Enfermedades crónicas', fmtLista(pp.enfermedades_cronicas));
    y = linea(doc, y, margen, 'Alergias', fmtLista(pp.alergias, 'sustancia'));
    y = linea(doc, y, margen, 'Cirugías', fmtLista(pp.cirugias, 'nombre'));
    y = linea(doc, y, margen, 'Hospitalizaciones', fmtLista(pp.hospitalizaciones, 'motivo'));
    y += 2;

    y = subseccion(doc, y, margen, 'Personales no patológicos');
    const pnp = expediente.antecedentes_personales_no_patologicos || {};
    y = linea(doc, y, margen, 'Tabaquismo', pnp.tabaquismo_estado || '—');
    y = linea(doc, y, margen, 'Alcoholismo', pnp.alcoholismo_estado || '—');
    y = linea(doc, y, margen, 'Actividad física', pnp.actividad_fisica || '—');
    y = linea(doc, y, margen, 'Dieta', pnp.dieta || '—');
    y = linea(doc, y, margen, 'Inmunizaciones', pnp.inmunizaciones || '—');
    y += 2;

    if (paciente.sexo === 'femenino') {
      y = checkPage(doc, y, margen);
      y = subseccion(doc, y, margen, 'Gineco-obstétricos');
      const go = expediente.antecedentes_gineco_obstetricos || {};
      y = linea(doc, y, margen, 'Menarca', go.menarca ? `${go.menarca} años` : '—');
      y = linea(doc, y, margen, 'FUM', fmt(go.fum));
      y = linea(doc, y, margen, 'Climaterio', go.climaterio ? `${go.climaterio} años` : '—');
      const formula = `G${go.gestaciones ?? 0} P${go.partos ?? 0} C${go.cesareas ?? 0} A${go.abortos ?? 0} O${go.obitos ?? 0} E${go.ectopicos ?? 0}`;
      y = linea(doc, y, margen, 'Historia obstétrica', formula);
      y += 2;
    }
  }

  // --- Consultas ---
  y = checkPage(doc, y, margen);
  y = seccion(doc, y, margen, pageWidth, `3. Consultas registradas (${consultas?.length || 0})`);
  for (const c of (consultas || []).slice(0, 20)) {
    y = checkPage(doc, y + 6, margen);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(99, 102, 241);
    doc.text(`${fmt(c.fecha_consulta)} — ${c.motivo_consulta || '(sin motivo)'}`, margen, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(30, 41, 59);
    if (c.padecimiento_actual) y = texto(doc, y, margen, `Padecimiento: ${c.padecimiento_actual}`, 9);
    if (c.signos_vitales && Object.keys(c.signos_vitales).length > 0) {
      y = texto(doc, y, margen, `Signos vitales: ${fmtObj(c.signos_vitales)}`, 9);
    }
    if (c.diagnosticos && c.diagnosticos.length > 0) {
      y = texto(doc, y, margen,
        `Dx: ${c.diagnosticos.map(d => `${d.codigo_cie10} ${d.descripcion_cie10}`).join('; ')}`,
        9
      );
    }
    if (c.plan_tratamiento) y = texto(doc, y, margen, `Plan: ${c.plan_tratamiento}`, 9);
    y += 3;
  }

  // --- Recetas ---
  if (recetas && recetas.length > 0) {
    y = checkPage(doc, y, margen);
    y = seccion(doc, y, margen, pageWidth, `4. Recetas emitidas (${recetas.length})`);
    for (const rx of recetas.slice(0, 30)) {
      y = checkPage(doc, y, margen);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text(`${rx.numero_receta} — ${fmt(rx.fecha_emision)}`, margen, y);
      y += 4;
      doc.setFont('helvetica', 'normal');
      const meds = Array.isArray(rx.medicamentos) ? rx.medicamentos : [];
      for (const m of meds) {
        const detalle = [
          m.nombre,
          m.dosis_mg && `${m.dosis_mg}mg`,
          m.via && `vía ${m.via}`,
          m.frecuencia_horas && `c/${m.frecuencia_horas}h`,
          m.duracion_dias && `× ${m.duracion_dias} días`,
        ].filter(Boolean).join(' ');
        y = texto(doc, y, margen, `• ${detalle}`, 9);
      }
      y += 2;
    }
  }

  // --- Footer en cada página ---
  const numPaginas = doc.internal.getNumberOfPages();
  for (let i = 1; i <= numPaginas; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(
      `Página ${i} de ${numPaginas} · NARA Care · Documento clínico confidencial`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 8,
      { align: 'center' }
    );
  }

  const nombre = (paciente.nombre_completo || 'paciente').replace(/\s+/g, '_');
  doc.save(`historia_clinica_${nombre}_${Date.now()}.pdf`);
}

// ===== Helpers =====

function seccion(doc, y, margen, pageWidth, titulo) {
  doc.setFillColor(238, 242, 255);
  doc.rect(margen - 2, y - 4, pageWidth - margen * 2 + 4, 8, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(30, 58, 95);
  doc.text(titulo, margen, y + 1);
  return y + 10;
}

function subseccion(doc, y, margen, titulo) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(99, 102, 241);
  doc.text(titulo, margen, y);
  return y + 5;
}

function linea(doc, y, margen, label, valor) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text(`${label}:`, margen, y);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(30, 41, 59);
  const texto = String(valor || '—');
  const split = doc.splitTextToSize(texto, 140);
  doc.text(split, margen + 48, y);
  return y + Math.max(5, split.length * 4.5);
}

function texto(doc, y, margen, txt, size = 9) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(size);
  doc.setTextColor(30, 41, 59);
  const split = doc.splitTextToSize(String(txt), 175);
  doc.text(split, margen, y);
  return y + split.length * 4.5;
}

function checkPage(doc, y, margen) {
  const max = doc.internal.pageSize.getHeight() - 15;
  if (y > max) {
    doc.addPage();
    return margen;
  }
  return y;
}

function calcEdad(iso) {
  if (!iso) return '—';
  const dob = new Date(iso);
  return Math.floor((Date.now() - dob.getTime()) / (365.25 * 24 * 3600 * 1000));
}

function fmt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtObj(obj) {
  if (!obj || typeof obj !== 'object') return '';
  return Object.entries(obj)
    .filter(([_, v]) => v !== null && v !== '' && v !== undefined)
    .map(([k, v]) => `${k} ${v}`)
    .join(', ');
}

function fmtLista(lista, key = 'nombre') {
  if (!lista) return '—';
  if (typeof lista === 'string') return lista;
  if (Array.isArray(lista) && lista.length > 0) {
    return lista
      .map(item => typeof item === 'string' ? item : (item[key] || JSON.stringify(item)))
      .join(', ');
  }
  return '—';
}

function formatearHeredoFamiliares(hf) {
  const etiquetas = {
    padre: 'Padre', madre: 'Madre',
    abuelos_paternos: 'Abuelos paternos', abuelos_maternos: 'Abuelos maternos',
    tios_tias: 'Tíos / Tías',
    // Compatibilidad con datos antiguos
    hermanos: 'Hermanos', hijos: 'Hijos',
  };
  const filas = [];
  for (const [fam, etiqueta] of Object.entries(etiquetas)) {
    if (hf[fam] && Array.isArray(hf[fam]) && hf[fam].length > 0) {
      filas.push(`${etiqueta}: ${hf[fam].join(', ')}`);
    }
  }
  if (hf.otros) filas.push(`Otros: ${hf.otros}`);
  return filas;
}
